import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ShareSpaceDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  propertyId: string;
  propertyTitle?: string;
}

const SITE_URL =
  typeof window !== "undefined" && window.location.hostname.endsWith("berastays.com")
    ? window.location.origin
    : "https://berastays.com";

/** Host-facing dialog that builds and shares a deep link to a single space. */
export default function ShareSpaceDialog({
  open,
  onOpenChange,
  propertyId,
  propertyTitle,
}: ShareSpaceDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const url = `${SITE_URL}/imovel/${propertyId}?ref=host`;
  const message = `${propertyTitle ? `${propertyTitle}\n` : ""}${t("shareSpace.message")}`;
  const full = `${message}\n${url}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(message);

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("—");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: propertyTitle || "BeraStays", text: message, url });
        return;
      } catch {
        /* cancelled */
      }
    }
    void copy(full, t("shareSpace.copied"));
  };

  const socials = [
    { name: "WhatsApp", color: "bg-[#25D366]", href: `https://wa.me/?text=${encodedText}%20${encodedUrl}` },
    { name: "Facebook", color: "bg-[#1877F2]", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { name: "X", color: "bg-black", href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}` },
    { name: "LinkedIn", color: "bg-[#0A66C2]", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] rounded-2xl p-5">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">{t("shareSpace.title")}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground leading-relaxed -mt-1">
          {t("shareSpace.subtitle")}
        </p>

        <div className="rounded-2xl bg-muted/50 border border-border/50 p-3 space-y-2">
          <p className="text-[13px] text-foreground leading-relaxed whitespace-pre-line break-words">
            {message}
          </p>
          <p className="text-[12px] text-primary break-all">{url}</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="h-11 rounded-xl text-xs font-semibold"
            onClick={() => copy(url, t("shareSpace.copied"))}
          >
            {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
            {t("shareSpace.copy")}
          </Button>
          <Button className="h-11 rounded-xl text-xs font-semibold" onClick={nativeShare}>
            <Share2 className="w-4 h-4 mr-1.5" />
            {t("shareSpace.share")}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-3 pt-1">
          {socials.map((s) => (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-sm transition-transform hover:scale-105",
                s.color,
              )}
              aria-label={s.name}
            >
              {s.name[0]}
            </a>
          ))}
        </div>

        <button
          onClick={() => copy(full, t("shareSpace.copied"))}
          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("shareSpace.copyText")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
